'use client';

import {
  Dialog,
  HStack,
  IconButton,
  Input,
  Portal,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Check, Pencil, Share2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export function ThreadHeader({
  title,
  titleTooltip = title,
  onRename,
  renaming = false,
  onShare,
}: {
  title: string;
  titleTooltip?: string;
  onRename?: (name: string) => Promise<boolean>;
  renaming?: boolean;
  onShare?: () => void;
}) {
  const inlineEditing = useBreakpointValue({ base: false, lg: true }) === true;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  const savingRef = useRef(false);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const save = async () => {
    const name = draft.trim();
    if (!name || !onRename || renaming || savingRef.current) return;
    if (name === title) {
      setEditing(false);
      return;
    }
    savingRef.current = true;
    try {
      if (await onRename(name)) setEditing(false);
    } finally {
      savingRef.current = false;
    }
  };

  const editor = (
    <>
      <Input
        ref={inputRef}
        aria-label="Thread title"
        autoFocus
        onFocus={event => event.currentTarget.select()}
        value={draft}
        disabled={renaming}
        size="sm"
        fontSize="13px"
        w="360px"
        minW={0}
        onChange={event => setDraft(event.target.value)}
        onKeyDown={event => {
          if (event.nativeEvent.isComposing) return;
          if (event.key === 'Enter') {
            event.preventDefault();
            void save();
          } else if (event.key === 'Escape' && !renaming) {
            event.preventDefault();
            setEditing(false);
          }
        }}
      />
      <IconButton
        aria-label="Save thread title"
        title="Save thread title"
        variant="ghost"
        size="xs"
        flexShrink={0}
        loading={renaming}
        disabled={!draft.trim()}
        onClick={() => void save()}
      >
        <Check size={16} />
      </IconButton>
      <IconButton
        aria-label="Cancel rename"
        title="Cancel rename"
        variant="ghost"
        size="xs"
        flexShrink={0}
        disabled={renaming}
        onClick={() => setEditing(false)}
      >
        <X size={16} />
      </IconButton>
    </>
  );

  return (
    <>
      <HStack gap="1" minW={0} maxW="full">
        {editing && inlineEditing ? (
          editor
        ) : (
          <>
            <Text
              fontSize="13px"
              fontWeight="550"
              color="ui.text.primary"
              truncate
              title={titleTooltip}
            >
              {title}
            </Text>
            {onRename ? (
              <IconButton
                aria-label="Rename thread"
                title="Rename thread"
                variant="ghost"
                size="xs"
                flexShrink={0}
                color="ui.text.secondary"
                onClick={() => {
                  setDraft(title);
                  setEditing(true);
                }}
              >
                <Pencil size={14} />
              </IconButton>
            ) : null}
          </>
        )}
        {onShare && (!editing || !inlineEditing) ? (
          <IconButton
            aria-label="Share thread"
            title="Share thread"
            variant="ghost"
            flexShrink={0}
            boxSize="38px"
            minW="38px"
            borderRadius="12px"
            _icon={{ boxSize: '16px' }}
            onClick={onShare}
          >
            <Share2 size={16} />
          </IconButton>
        ) : null}
      </HStack>
      {!inlineEditing ? (
        <Dialog.Root
          open={editing}
          onOpenChange={({ open }) => {
            if (!renaming) setEditing(open);
          }}
          initialFocusEl={() => inputRef.current}
          closeOnEscape={!renaming}
          closeOnInteractOutside={!renaming}
          placement="center"
          size="sm"
        >
          <Portal>
            {editing ? (
              <>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                  <Dialog.Content maxW="calc(100vw - 32px)">
                    <Dialog.Header>
                      <Dialog.Title>Rename thread</Dialog.Title>
                    </Dialog.Header>
                    <Dialog.Body pb="6">
                      <HStack gap="1" minW={0}>
                        {editor}
                      </HStack>
                    </Dialog.Body>
                  </Dialog.Content>
                </Dialog.Positioner>
              </>
            ) : null}
          </Portal>
        </Dialog.Root>
      ) : null}
    </>
  );
}
